import PropTypes from 'prop-types';
// @flow

import React from 'react';
import _ from 'lodash';

import I18n from 'PMT/i18n/I18n';

// -- constants
import RoutingEnum from 'constants/RoutingEnum';
import ViewConstants from 'constants/ViewConstants';
import ApiConstants from 'PMT/constants/ApiConstants';

import SecurityContext from 'utils/permissions/SecurityContext';

// -- actions
import RestaurantMenuAction from 'actions/RestaurantMenuAction';

// -- entities
import Product from 'PMT/entities/menu/Product';
import RestaurantMenu from 'PMT/entities/menu/RestaurantMenu';
import ProductOption from 'PMT/entities/menu/ProductOption';
import ProductOptionValue from 'PMT/entities/menu/ProductOptionValue';

// -- views
import AbstractRestaurantMenuForm from '../components/AbstractRestaurantMenuForm';
import Link from 'PMT/components/Link';
import { FixedSubmitArea, ImageDropzone } from 'PMT/components/form';
import ProductOptionProductRow from './components/ProductOptionProductRow';
import ProductOptionValueRow from './components/ProductOptionValueRow';

export default class ProductOptionDetailView extends AbstractRestaurantMenuForm {
  static propTypes = {
    productOption: PropTypes.instanceOf(ProductOption).isRequired,

    restaurantMenu: PropTypes.instanceOf(RestaurantMenu).isRequired,

    /**
     * @param: ProductOption
     */
    onUpdate: PropTypes.func.isRequired,

    currencyCode: PropTypes.string,

  };

  componentWillReceiveProps(nextProps: Object) {
    super.componentWillReceiveProps(nextProps);
  }

  shouldUpdateState(nextProps: Object) {
    return !this.props.productOption.equals(nextProps.productOption);
  }

  getDefaultState(props: Object) {
    return {
      productOption: props.productOption.copy(),

      image: null,

      displayEditModal: false,
    };
  }

  get productOption(): ProductOption {
    return this.state.productOption;
  }

  get restaurantMenu(): RestaurantMenu {
    return this.props.restaurantMenu;
  }

  get currencyCode(): string {
    return this.props.currencyCode;
  }


  get haveModifications(): boolean {
    return (
      !this.props.productOption.equals(this.state.productOption) ||
      !_.isNull(this.state.image)
    );
  }

  //
  // Events
  //

  onProductOptionChange = (productOption: ProductOption) => {
    this.setState({
      productOption,
    });
  };

  //
  // Events form
  //

  onSubmit = () => {
    this.props.onUpdate(this.productOption, this.state.image);
  };

  onReset = () => {
    this.setState({
      productOption: this.props.productOption.copy(),
    });
  };

  //
  // Events image
  //

  onImageChange = (pictureData: string) => {
    this.setState({
      image: pictureData,
    });
  };

  //
  // renderers
  //

  renderImage() {
    let readOnly =
      SecurityContext.restaurantMenu.canEdit() && false ? false : true;

    return (
      <ImageDropzone
        readOnly={readOnly}
        placeholder={
          ViewConstants.Image.RestaurantMenu.NO_IMAGE_FOR_PRODUCT_OPTION
        }
        image={this.productOption.image}
        currentImage={this.state.image}
        onChange={this.onImageChange}
        aspectRatio={ApiConstants.Image.RestaurantMenu.ProductOption.RATIO}
        width={ApiConstants.Image.RestaurantMenu.ProductOption.WIDTH}
        height={ApiConstants.Image.RestaurantMenu.ProductOption.HEIGHT}
        maxSize={ApiConstants.Image.RestaurantMenu.ProductOption.MAX_SIZE}
      />
    );
  }

  renderEditButton() {
    if (!SecurityContext.restaurantMenu.canEdit()) {
      return null;
    }

    return (
      <Link
        className="btn btn-primary"
        to={RoutingEnum.RESTAURANT_MENU_PRODUCT_OPTION_EDIT}
        params={{ productOptionId: this.productOption.id }}
      >
        <i className="fa fa-edit" />
        &nbsp;&nbsp;
        {I18n.tr('menu.product_option.edit')}
      </Link>
    );
  }

  renderOptionValuesList() {
    const partsListView = this.productOption.values.map(
      (productOptionValue: ProductOptionValue) => {
        return (
          <ProductOptionValueRow
            key={productOptionValue.id}
            productOptionValue={productOptionValue}
            restaurantMenu={this.restaurantMenu}
            currencyCode={this.currencyCode}
          />
        );
      }
    );

    return (
      <div className="">
        <ul>{partsListView}</ul>
      </div>
    );
  }

  renderProductsList() {
    const products: Array<Product> = this.restaurantMenu.getProductsWithOptionId(
      this.productOption.id
    );

    if (_.isEmpty(products)) {
      return (
        <div>
          <em>{I18n.tr('menu.product_option.no_products_with_option')}</em>
        </div>
      );
    }

    const productsListView = [];

    _.each(products, (product: Product) => {
      productsListView.push(
        <ProductOptionProductRow key={product.id} product={product} />
      );
    });

    return (
      <div>
        <ul>{productsListView}</ul>
      </div>
    );
  }

  renderQuantityLabel() {
    if (this.productOption.quantityMin >= 1) {
      return (
        <span className="label label-danger">
          {I18n.tr('menu.product_option.required')}
        </span>
      );
    }

    return (
      <span className="label label-info">
        {I18n.tr('menu.product_option.optionnal')}
      </span>
    );
  }

  renderSubmitArea() {
    return (
      <FixedSubmitArea
        haveModifications={this.haveModifications}
        onSubmit={this.onSubmit}
        onReset={this.onReset}
      />
    );
  }

  render() {
    return (
      <div className="product_option_view">
        <div className="row">
          <div className="col-xs-12 col-sm-3">
            <h3>{this.productOption.name}</h3>
            <h4>
              {this.renderQuantityLabel()}
              &nbsp;&nbsp;
              {I18n.tr('menu.product_option.min_with_colon')}{' '}
              {this.productOption.quantityMin} |{' '}
              {I18n.tr('menu.product_option.max_with_colon')}{' '}
              {this.productOption.quantityMax}
            </h4>
          </div>

          <div className="hidden-xs col-sm-6">{this.renderImage()}</div>

          <div className="col-xs-2 col-sm-3">{this.renderEditButton()}</div>
        </div>

        <hr />

        <div className="row">
          <div className="top-buffer-40 col-xs-12">
            <h2>{I18n.tr('menu.product_option_view.choices')}</h2>

            {this.renderOptionValuesList()}
          </div>
        </div>

        <div className="row">
          <div className="top-buffer-40 col-xs-12">
            <h2>{I18n.tr('menu.product_option_view.products')}</h2>
            <em>{I18n.tr('menu.product_option_view.products_with_option')}</em>

            {this.renderProductsList()}
          </div>
        </div>

        {this.renderSubmitArea()}
      </div>
    );
  }
}
